import { useEffect, useRef } from "react";
import type { PlayerData } from "@/components/game/PlayerArea";

interface AutoplayDeps {
  isMyTurn: boolean;
  turnState: string;
  myPlayerData: PlayerData | undefined;
  flipThreeRemaining: number;
  pendingAction: { type: string; value: number | null; subtype: string | null } | null;
  players: PlayerData[];
  currentUsername: string;
  sendAction: (action: string, extra?: Record<string, unknown>) => void;
}

const AUTOPLAY_DELAY_MS = 1500;
const SAFE_STAY_THRESHOLD = 3;
const AGGRESSIVE_STAY_THRESHOLD = 5;

function pickTarget(
  subtype: string | null,
  players: PlayerData[],
  currentUsername: string,
): string | null {
  const activePlayers = players.filter((p) => p.status === "active");
  if (activePlayers.length === 0) return null;

  if (subtype === "second_chance") {
    const validTargets = activePlayers.filter(
      (p) => !p.action_cards.some((c) => c.subtype === "second_chance"),
    );
    const self = validTargets.find((p) => p.username === currentUsername);
    if (self) return self.username;
    return validTargets[0]?.username ?? null;
  }

  if (subtype === "freeze") {
    const others = activePlayers.filter((p) => p.username !== currentUsername);
    if (others.length === 0) return activePlayers[0]?.username ?? null;
    others.sort((a, b) => b.round_score - a.round_score);
    return others[0].username;
  }

  if (subtype === "flip_three") {
    const others = activePlayers.filter((p) => p.username !== currentUsername);
    if (others.length === 0) return activePlayers[0]?.username ?? null;
    others.sort((a, b) => b.unique_number_count - a.unique_number_count);
    return others[0].username;
  }

  return activePlayers[0]?.username ?? null;
}

export function useAutoplay({
  isMyTurn,
  turnState,
  myPlayerData,
  flipThreeRemaining,
  pendingAction,
  players,
  currentUsername,
  sendAction,
}: AutoplayDeps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!isMyTurn || !myPlayerData?.autoplay_mode) return;

    const mode = myPlayerData.autoplay_mode;

    if (turnState === "waiting_for_target" && pendingAction) {
      timerRef.current = setTimeout(() => {
        const target = pickTarget(pendingAction.subtype, players, currentUsername);
        if (target) sendAction("select_target", { target });
      }, AUTOPLAY_DELAY_MS);
      return;
    }

    if (turnState === "resolving_flip_three") {
      timerRef.current = setTimeout(() => {
        sendAction("hit");
      }, AUTOPLAY_DELAY_MS);
      return;
    }

    if (turnState === "waiting_for_decision") {
      const threshold = mode === "safe" ? SAFE_STAY_THRESHOLD : AGGRESSIVE_STAY_THRESHOLD;
      const hasCards = (myPlayerData.number_cards.length > 0 || myPlayerData.modifier_cards.length > 0);
      const shouldStay = hasCards && myPlayerData.unique_number_count >= threshold;

      timerRef.current = setTimeout(() => {
        sendAction(shouldStay ? "stay" : "hit");
      }, AUTOPLAY_DELAY_MS);
      return;
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isMyTurn, turnState, myPlayerData, flipThreeRemaining, pendingAction, players, currentUsername, sendAction]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);
}
